import { useIntl } from '@weenat/wintl';
import flatten from 'lodash-es/flatten';
import groupBy from 'lodash-es/groupBy';
import intersection from 'lodash-es/intersection';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import { useEffect, useMemo, useState } from 'react';
import { useClient } from '../../ClientProvider';
import { useHistoryContext } from '../../HistoryContext';
import { useItemContext } from '../../ItemContext';
import DataSources, { isForecastDataSource } from '../../enums/DataSources';
import { hydricStressMetrics, isAggregateEnabledMetric, isHydricStress, isMetricWithHorizon } from '../../enums/MetricIds';
import TimeSteps from '../../enums/TimeSteps';
import useConvertedValue from '../../hooks/useConvertedValue';
import { useGetCurrentThresholdsProfile } from '../../hooks/useThresholds';
import { mergeQueryState } from '../../mergeState';
import { toBackEndCumulativeSumMetric } from '../../resources/cumulativeSum';
import { SoilTexture } from '../../resources/plots';
import { thresholdTypes } from '../../resources/thresholds.types';
import useQuery from '../../useQuery';
import { thresholdColorScale } from '../dss/irrigation';
import { isCustomThresholds } from '../dss/soilTexture';
import { getAccumulationMetricFromCumulativeSumDssView, getAggregateDataForMetric, getMetricsToFetch, isCumulativeSumDssView, metricsAllowingAggregatesOnMaxTimestep, timestampToMoment } from '../history';
import { defaultTimezone } from '../history/datesUtilities';
import { denormalizeDataPoint, formatCumulativeSumHistoryForCharts, formatHistoryDataForCharts, formatHorizonHistoryDataForCharts, getAggregatesFromChartData, getChartDomains, isDataSetNull, normalizeDataPoint } from './charts';
const IRRITENSIO_STRESS_VALUE = 125;
const CAPACITIVE_SATURATION_VALUE = 50;
const metricsDisplayedInBars = [
    'IRR',
    'RR',
    'etp',
    'irrigation'
];
const horizonDataSetToDataSet = (horizonDataset) => flatten(Object.values(horizonDataset));
const useDeviceHistoryData = ({ deviceId, enabled, selectedMetrics, selectedPeriod: period, selectedTimeStep, selectedDssView, base, gdd_t_max }) => {
    const client = useClient();
    const metricsToFetch = useMemo(() => getMetricsToFetch(selectedMetrics), [selectedMetrics]);
    const measurementHistoryRequest = useQuery(client.measurements.devices.histories.get1_1({
        deviceId,
        ...period,
        span: selectedTimeStep,
        metrics: metricsToFetch
    }), { enabled: enabled && !isEmpty(metricsToFetch) });
    const cumulativeSumRequest = useQuery(client.cumulativeSum.history({
        deviceId,
        base,
        gdd_t_max,
        metric: selectedDssView === 'RR_ACCUMULATION' ? 'RRAccumulation' : 'heatAccumulation',
        ...period
    }), {
        enabled: isCumulativeSumDssView(selectedDssView) && enabled
    });
    return { measurementHistoryRequest, cumulativeSumRequest };
};
const usePlotHistoryData = ({ plot, enabled, aggregateHorizon, selectedMetrics, selectedPeriod: period, selectedTimeStep, selectedDssView, base, gdd_t_max, aggregateByMetric, thresholdsProfile: thresholdsProfileFromContext, selectedSource, canUseVPDFromDevice }) => {
    const client = useClient();
    const metricsToFetch = useMemo(() => getMetricsToFetch(selectedMetrics), [selectedMetrics]);
    const fetchVPD = selectedDssView === 'VPD';
    const plotId = plot?.id;
    const measurementHistoryRequest = useQuery(client.measurements.plots.histories.get1_1({
        plotId,
        ...period,
        span: selectedTimeStep,
        metrics: metricsToFetch,
        aggregateHorizon,
        aggregateByMetric
    }), { enabled: enabled && !isEmpty(metricsToFetch) && !fetchVPD });
    const cumulativeSumRequest = useQuery(client.cumulativeSum.history({
        plotId,
        base,
        gdd_t_max,
        metric: isCumulativeSumDssView(selectedDssView)
            ? getAccumulationMetricFromCumulativeSumDssView(selectedDssView)
            : undefined,
        ...period
    }), {
        enabled: enabled && isCumulativeSumDssView(selectedDssView)
    });
    const vpdHistoryRequest = useQuery(client.vpd.history({
        plotId,
        ...period,
        span: selectedTimeStep,
        source: canUseVPDFromDevice ? selectedSource ?? DataSources.measurement : undefined
    }), { enabled: enabled && fetchVPD });
    const { requestState: thresholdProfileRequestState, thresholdsProfile } = useGetCurrentThresholdsProfile(plot, undefined, enabled && isNil(thresholdsProfileFromContext));
    return {
        measurementHistoryRequest: fetchVPD ? vpdHistoryRequest : measurementHistoryRequest,
        cumulativeSumRequest,
        thresholdsRequest: thresholdProfileRequestState,
        thresholds: !isNil(thresholdsProfileFromContext)
            ? thresholdsProfileFromContext.thresholds
            : thresholdsProfile?.thresholds
    };
};
export const getColorGradientLimits = ({ yDomain, absoluteYDomain }) => {
    const maximumAbsoluteRange = Math.abs(absoluteYDomain[1] - absoluteYDomain[0]);
    const maximumRelativeRange = Math.abs(yDomain[1] - yDomain[0]);
    const differenceBetweenMin = Math.abs(yDomain[0] - absoluteYDomain[0]);
    const differenceBetweenMax = Math.abs(yDomain[1] - absoluteYDomain[1]);
    const ratio = maximumAbsoluteRange / maximumRelativeRange;
    const gradientStart = -differenceBetweenMax * ratio;
    const gradientEnd = 100 + differenceBetweenMin * ratio;
    return { gradientStart, gradientEnd };
};
const defaultReturn = {
    displayableMetrics: [],
    areAllDataSetNull: true,
    historyChartDomain: {
        x: [new Date(), new Date()],
        y: [0, 1]
    },
    horizons: { isAggregateBySerial: false, horizons: [] },
    historyData: { values: {} },
    isLoading: false,
    period: { since: 0, before: 0 },
    timeStep: TimeSteps.perDays,
    timezone: defaultTimezone,
    dmDisplayedInBars: [],
    dmNotDisplayedInBars: [],
    hasTwoBarMetrics: false,
    texture: null,
    refresh: () => {
        return;
    }
};
const getInfos = (data, formatConvertedValueFct) => {
    // Filtering out forecast values to compute aggregates
    const aggregates = getAggregatesFromChartData(data.filter((point) => !isNil(point.source) && !isForecastDataSource(point.source)));
    const initialInfos = {
        min: null,
        max: null,
        avg: null,
        sum: null
    };
    const infos = Object.entries(aggregates).reduce((acc, [key, value]) => ({
        ...acc,
        [key]: formatConvertedValueFct(value)
    }), initialInfos);
    return infos;
};
/** @deprecated chartWithD3 */
const useHistoryChart = (item, canUseVPDFromDevice) => {
    const { id, timezone, kind } = useItemContext();
    const { historyState, dispatch } = useHistoryContext();
    const { t } = useIntl();
    const soilTexture = 'soilTexture' in item ? item.soilTexture : null;
    const isPlot = kind === 'plot';
    const { selectedPeriod: period, selectedMetrics, selectedDssView, selectedTimeStep: timeStep, isAggregateBySerial, aggregateByMetric, dataToDisplay, selectedMeasurementReports, waterIncome, highlightThresholds, base, GDDTMax, thresholdsProfile, selectedSource } = historyState;
    if (aggregateByMetric && isAggregateBySerial) {
        console.error(`historyState has both aggregateByMetric & isAggregateBySerial set to true, but they are not compatible`);
    }
    const { measurementHistoryRequest: plotMeasurementHistoryRequest, cumulativeSumRequest: plotCumulativeSumRequest, thresholdsRequest, thresholds: thresholdsResults } = usePlotHistoryData({
        plot: item,
        enabled: isPlot,
        aggregateHorizon: isAggregateBySerial ? false : undefined,
        aggregateByMetric,
        base,
        gdd_t_max: GDDTMax,
        selectedDssView,
        selectedPeriod: period,
        selectedMetrics,
        selectedTimeStep: timeStep,
        thresholdsProfile,
        selectedSource,
        canUseVPDFromDevice
    });
    const { measurementHistoryRequest: deviceMeasurementHistoryRequest, cumulativeSumRequest: deviceCumulativeSumRequest } = useDeviceHistoryData({
        deviceId: id,
        enabled: !isPlot,
        base,
        gdd_t_max: GDDTMax,
        selectedDssView,
        selectedPeriod: period,
        selectedMetrics,
        selectedTimeStep: timeStep
    });
    const measurementHistoryRequest = isPlot
        ? plotMeasurementHistoryRequest
        : deviceMeasurementHistoryRequest;
    const cumulativeSumRequest = isPlot ? plotCumulativeSumRequest : deviceCumulativeSumRequest;
    const historyData = measurementHistoryRequest.data;
    const { isLoading, isFetching } = mergeQueryState(measurementHistoryRequest, cumulativeSumRequest, thresholdsRequest);
    const { getUnit, convertNumberValue, convertValue, formatConvertedValue } = useConvertedValue();
    const sinceAsMoment = timestampToMoment(period.since, timezone);
    const beforeAsMoment = timestampToMoment(period.before, timezone);
    const historyChartDomain = {
        x: [sinceAsMoment.toDate(), beforeAsMoment.toDate()],
        // scaling from 0 to 1 to normalize both axis
        y: [0, 1]
    };
    const hasTwoBarMetrics = intersection([...selectedMetrics, ...selectedMeasurementReports], metricsDisplayedInBars)
        .length >= 2;
    const isIrritensio = selectedMetrics.includes('HPOT');
    const texture = !isNil(soilTexture)
        ? isCustomThresholds({
            currentThresholds: thresholdsResults,
            soilTexture,
            isIrritensio
        })
            ? SoilTexture.custom
            : soilTexture
        : null;
    /** This is to store effects generated inside {@link hookState} otherwise if we dispatch during render we will get a warning */
    const [effects, setEffects] = useState([]);
    const hookState = useMemo(() => {
        if (!isLoading) {
            let hpotOrCapaNormalizeFct;
            let hpotOrCapaDenormalizeFct;
            const metricWithHorizon = selectedMetrics.find((m) => isMetricWithHorizon(m, aggregateByMetric));
            const horizons = isAggregateBySerial
                ? {
                    isAggregateBySerial,
                    colorBySerialByMetric: measurementHistoryRequest.data?.colorBySerial
                }
                : {
                    isAggregateBySerial,
                    horizons: metricWithHorizon && isMetricWithHorizon(metricWithHorizon, aggregateByMetric) //dumb test i know but this one is for typescript
                        ? Object.values(measurementHistoryRequest.data?.horizons?.[metricWithHorizon] || {}) || []
                        : []
                };
            let displayableMetrics = selectedMetrics.map((metric) => {
                const hasAggregates = isAggregateEnabledMetric(metric);
                let aggregatesData = {
                    min: [],
                    max: []
                };
                const metricUnit = getUnit(metric);
                const conversionFct = (value) => convertNumberValue({ metric, value });
                const formatConvertedValueFct = (value) => formatConvertedValue({ metric, value });
                let result;
                let horizonDataSet = {};
                let dataSet = [];
                if (!isNil(historyData) && !isEmpty(metric)) {
                    if (hasAggregates &&
                        (timeStep !== TimeSteps.maximum ||
                            metricsAllowingAggregatesOnMaxTimestep.includes(metric))) {
                        aggregatesData = getAggregateDataForMetric(historyData, metric, conversionFct);
                    }
                    if (isMetricWithHorizon(metric, aggregateByMetric)) {
                        horizonDataSet = formatHorizonHistoryDataForCharts({
                            metric,
                            data: historyData,
                            conversionFct,
                            isAggregateBySerial,
                            colorBySerial: horizons?.colorBySerialByMetric,
                            dataToDisplay
                        });
                        if (isEmpty(historyState.dataToDisplay)) {
                            setEffects([
                                () => dispatch({
                                    type: 'initDataToDisplay',
                                    ids: isAggregateBySerial
                                        ? Object.keys(horizonDataSet)
                                        : Object.keys(horizonDataSet).map((k) => parseInt(k, 10))
                                })
                            ]);
                        }
                    }
                    else {
                        dataSet = formatHistoryDataForCharts({
                            data: historyData,
                            metric,
                            conversionFct
                        });
                    }
                    const data = hasAggregates
                        ? // if min and max are null for some point that could lead to wrong domain
                            // so we create a set using avg min and max to be sure that domain remains correct every time
                            flatten(Object.values(aggregatesData)).concat(dataSet)
                        : isMetricWithHorizon(metric, aggregateByMetric)
                            ? horizonDataSetToDataSet(horizonDataSet)
                            : dataSet;
                    const { absoluteYDomain, yDomain, xDomain: currentXDomain } = getChartDomains({
                        metric,
                        timeStep,
                        unit: metricUnit,
                        data,
                        defaultXDomain: historyChartDomain.x,
                        customMax: highlightThresholds
                            ? metric === 'U_CAPA'
                                ? CAPACITIVE_SATURATION_VALUE
                                : metric === 'HPOT'
                                    ? Math.min(Math.max(IRRITENSIO_STRESS_VALUE, thresholdsResults?.stress ?? Number.MIN_SAFE_INTEGER) * 1.1, 200)
                                    : undefined
                            : undefined,
                        customMin: metric === 'U_CAPA' ? thresholdsResults?.stress : undefined
                    });
                    historyChartDomain.x = currentXDomain;
                    const infos = getInfos(data, formatConvertedValueFct);
                    const normalizeScale = normalizeDataPoint(yDomain);
                    const denormalizeScale = denormalizeDataPoint(yDomain);
                    Object.values(horizonDataSet).forEach((points) => points.forEach((point) => (point.y = normalizeScale(point.value))));
                    const denormalizeFct = (point) => denormalizeScale(point);
                    const normalizeFct = (point) => normalizeScale(point);
                    const getGradient = () => getColorGradientLimits({ yDomain, absoluteYDomain });
                    if (isHydricStress(metric)) {
                        hpotOrCapaNormalizeFct = normalizeFct;
                        hpotOrCapaDenormalizeFct = denormalizeFct;
                    }
                    // could not factorise that as typescript is lost when specifying different dataset inside same object
                    if (isMetricWithHorizon(metric, aggregateByMetric)) {
                        result = {
                            absoluteYDomain,
                            dataSet: horizonDataSet,
                            hasAggregates,
                            infos,
                            metricUnit,
                            metricId: metric,
                            yDomain: [0, 1],
                            denormalizeFct,
                            normalizeFct,
                            getGradient,
                            allData: data
                        };
                    }
                    else {
                        result = {
                            absoluteYDomain,
                            aggregatesData: buildAggregatesData(aggregatesData, yDomain),
                            dataSet: dataSet.map((point) => ({
                                ...point,
                                y: normalizeScale(point.value)
                            })),
                            hasAggregates,
                            infos,
                            metricUnit,
                            metricId: metric,
                            yDomain: [0, 1],
                            denormalizeFct,
                            normalizeFct,
                            getGradient,
                            allData: data
                        };
                    }
                }
                else {
                    result = {
                        absoluteYDomain: [0, 0],
                        aggregatesData: [],
                        dataSet: [],
                        hasAggregates: false,
                        infos: { min: null, max: null, avg: null, sum: null },
                        metricUnit,
                        metricId: metric,
                        yDomain: [0, 0],
                        denormalizeFct: () => 0,
                        normalizeFct: () => 0,
                        getGradient: () => ({ gradientStart: 0, gradientEnd: 100 }),
                        allData: []
                    };
                }
                return result;
            });
            const isShowingIrriCapaTensio = intersection([selectedDssView], hydricStressMetrics).length > 0;
            const isRainfallsHidden = !waterIncome.includes('rainfalls');
            const isIrrigationHidden = !waterIncome.includes('irrigation');
            // we don't want to display RR if user has unselected rainfall from irricaption
            displayableMetrics =
                isShowingIrriCapaTensio && (isRainfallsHidden || isIrrigationHidden)
                    ? displayableMetrics.reduce((acc, dm) => {
                        if (!((dm.metricId === 'RR' && isRainfallsHidden) ||
                            (dm.metricId === 'IRR' && isIrrigationHidden))) {
                            acc.push(dm);
                        }
                        return acc;
                    }, [])
                    : displayableMetrics;
            addCumulativeSum({
                cumulativeSumRequest,
                selectedDssView,
                convertNumberValue,
                convertValue,
                timeStep,
                getUnit,
                displayableMetrics
            });
            const areAllDataSetNull = displayableMetrics.every(({ dataSet }) => Array.isArray(dataSet)
                ? isDataSetNull(dataSet)
                : isDataSetNull(horizonDataSetToDataSet(dataSet)));
            const displayedUnits = displayableMetrics.map((dm) => dm.metricUnit);
            /**
             * when multiple metrics share the same unit we want to use only one axis
             * so we re-compute the domain and re-apply the translation method
             */
            const duplicatedUnits = [
                ...new Set(displayedUnits.filter((value, index, self) => self.indexOf(value) !== index))
            ];
            if (!isEmpty(duplicatedUnits)) {
                duplicatedUnits.forEach((duplicate) => {
                    const dmsWithSameUnit = displayableMetrics.filter((dm) => dm.metricUnit === duplicate);
                    const metric = dmsWithSameUnit[0].metricId;
                    const finalMetric = metric === 'irrigation' || metric === 'rainfalls'
                        ? 'RR'
                        : metric === 'dryMatter'
                            ? 'U'
                            : metric;
                    const concatenatedData = dmsWithSameUnit.reduce((acc, dm) => {
                        acc.push(...dm.allData);
                        return acc;
                    }, []);
                    const chartDomainWithDuplicatedUnits = getChartDomains({
                        data: concatenatedData,
                        metric: finalMetric,
                        timeStep,
                        unit: duplicate
                    });
                    const normalizeScale = normalizeDataPoint(chartDomainWithDuplicatedUnits.yDomain);
                    const denormalizeScale = denormalizeDataPoint(chartDomainWithDuplicatedUnits.yDomain);
                    dmsWithSameUnit.forEach((dm) => {
                        const metric = dm.metricId;
                        dm.yDomain = chartDomainWithDuplicatedUnits.yDomain;
                        const denormalizeFct = (point) => denormalizeScale(point);
                        const normalizeFct = (point) => normalizeScale(point);
                        dm.normalizeFct = normalizeFct;
                        dm.denormalizeFct = denormalizeFct;
                        if (Array.isArray(dm.dataSet)) {
                            dm.dataSet.forEach((point) => {
                                point.y = normalizeFct(point.value);
                            });
                        }
                        else {
                            Object.values(dm.dataSet).forEach((points) => {
                                points.forEach((point) => (point.y = normalizeFct(point.value)));
                            });
                        }
                        if ('aggregatesData' in dm) {
                            const hasAggregates = dm.hasAggregates;
                            let aggregatesData = {
                                min: [],
                                max: []
                            };
                            const conversionFct = (value) => convertNumberValue({ metric, value });
                            if (hasAggregates &&
                                (timeStep !== TimeSteps.maximum ||
                                    metricsAllowingAggregatesOnMaxTimestep.includes(metric)) &&
                                historyData &&
                                isAggregateEnabledMetric(metric)) {
                                aggregatesData = getAggregateDataForMetric(historyData, metric, conversionFct);
                            }
                            dm.aggregatesData = buildAggregatesData(aggregatesData, chartDomainWithDuplicatedUnits.yDomain);
                        }
                    });
                });
            }
            const dmByDisplayedInBar = groupBy(displayableMetrics, (dm) => metricsDisplayedInBars.includes(dm.metricId));
            /** Thresholds Areas */
            const thresholds = !isNil(thresholdsResults) &&
                !isNil(hpotOrCapaNormalizeFct) &&
                historyState.thresholdsDisplayed
                ? thresholdTypes.reduce((acc, thresholdName) => {
                    acc[thresholdName] = {
                        color: thresholdColorScale(thresholdName),
                        data: historyChartDomain.x.map((date) => {
                            //just for TS because already check before
                            if (!isNil(hpotOrCapaNormalizeFct) && !isNil(hpotOrCapaDenormalizeFct)) {
                                let value;
                                let nextValue;
                                let label = t(`dss.irritensio.flags.${thresholdName}`);
                                if (isIrritensio) {
                                    nextValue = thresholdsResults[thresholdName];
                                    if (thresholdName === 'saturation') {
                                        value = thresholdsResults['comfort'];
                                    }
                                    else if (thresholdName === 'comfort') {
                                        value = thresholdsResults['vigilance'];
                                    }
                                    else if (thresholdName === 'vigilance') {
                                        value = thresholdsResults['stress'];
                                    }
                                    else {
                                        // this value will be overrided below
                                        value = hpotOrCapaDenormalizeFct(historyChartDomain.y[1]);
                                        if (!isNil(value) && !isNil(nextValue) && value < nextValue) {
                                            value = nextValue;
                                        }
                                    }
                                }
                                else {
                                    if (thresholdName === 'saturation') {
                                        value = CAPACITIVE_SATURATION_VALUE;
                                        nextValue = thresholdsResults['saturation'];
                                    }
                                    else if (thresholdName === 'comfort') {
                                        value = thresholdsResults['saturation'];
                                        nextValue = thresholdsResults['comfort'];
                                    }
                                    else if (thresholdName === 'vigilance') {
                                        value = thresholdsResults['comfort'];
                                        nextValue = thresholdsResults['vigilance'];
                                    }
                                    else {
                                        label = t('dss.capacitiveIrrigation.dss.stressLabel');
                                        // Not using stress here as for capacitive irrigation stress represents the "Point de fletrissement"
                                        value = thresholdsResults['vigilance'];
                                        // May need to be the value for stress
                                        nextValue = highlightThresholds ? thresholdsResults['stress'] : 0;
                                    }
                                }
                                const point = {
                                    color: thresholdColorScale(thresholdName),
                                    label,
                                    metric: isIrritensio ? 'HPOT' : 'U_CAPA',
                                    timestamp: date.getTime() / 1000,
                                    x: date,
                                    y: hpotOrCapaNormalizeFct(value),
                                    y0: hpotOrCapaNormalizeFct(nextValue),
                                    value,
                                    nextValue
                                };
                                return point;
                            }
                            else {
                                throw new Error('No normalize function were found for thresholds');
                            }
                        })
                    };
                    return acc;
                }, {})
                : undefined;
            const result = {
                displayableMetrics,
                areAllDataSetNull,
                hasTwoBarMetrics,
                historyChartDomain,
                horizons,
                historyData,
                isLoading,
                timezone,
                timeStep,
                period,
                dmDisplayedInBars: dmByDisplayedInBar['true'] || [],
                dmNotDisplayedInBars: dmByDisplayedInBar['false'] || [],
                thresholds,
                texture,
                refresh: measurementHistoryRequest.refetch
            };
            return result;
        }
        else {
            return { ...defaultReturn, isLoading };
        }
    }, [JSON.stringify(historyState), isLoading, texture, isFetching, item.id]);
    useEffect(() => {
        if (effects.length) {
            effects.forEach((eff) => eff());
            setEffects([]);
        }
    }, [effects]);
    return hookState;
};
export default useHistoryChart;
/**
 * Checks if the view is a cumulative sum dss
 * Adds a cumulative sum to displayable metrics
 */
const addCumulativeSum = ({ cumulativeSumRequest, selectedDssView, convertNumberValue, convertValue, timeStep, getUnit, displayableMetrics }) => {
    const cumulativeSumHistory = cumulativeSumRequest.data?.cumulativeSum;
    if (cumulativeSumHistory && isCumulativeSumDssView(selectedDssView)) {
        const emptyLessCumulativeSum = Object.values(cumulativeSumHistory).filter((cmEntry) => cmEntry !== null);
        const metric = getAccumulationMetricFromCumulativeSumDssView(selectedDssView);
        const conversionFct = (value) => convertNumberValue({ metric, value });
        const convertValueFct = (value) => convertValue({ metric, value, displayUnit: false });
        const sum = !isEmpty(emptyLessCumulativeSum)
            ? convertValueFct(emptyLessCumulativeSum[emptyLessCumulativeSum.length - 1])
            : null;
        const formattedDataPoints = formatCumulativeSumHistoryForCharts({
            conversionFct,
            data: cumulativeSumHistory,
            metric: toBackEndCumulativeSumMetric(metric)
        });
        const { absoluteYDomain, yDomain } = getChartDomains({
            metric,
            timeStep,
            unit: getUnit(metric),
            data: formattedDataPoints
        });
        const normalizeScale = normalizeDataPoint(yDomain);
        const denormalizeScale = denormalizeDataPoint(yDomain);
        const displayableCumulativeSum = {
            absoluteYDomain,
            aggregatesData: [],
            dataSet: formattedDataPoints.map((point) => ({
                ...point,
                y: normalizeScale(point.value)
            })),
            hasAggregates: false,
            infos: { min: null, max: null, avg: null, sum },
            metricUnit: getUnit(metric),
            metricId: metric,
            yDomain,
            denormalizeFct: (point) => denormalizeScale(point),
            normalizeFct: (point) => normalizeScale(point),
            getGradient: () => ({ gradientStart: 0, gradientEnd: 100 }),
            allData: formattedDataPoints
        };
        displayableMetrics.push(displayableCumulativeSum);
    }
};
function buildAggregatesData(aggregatesData, yDomain) {
    const normalizeScale = normalizeDataPoint(yDomain);
    return aggregatesData.max.map((point, i) => ({
        ...point,
        y: normalizeScale(point.value),
        y0: normalizeScale(aggregatesData.min[i].value)
    }));
}
/**
 * Goes through the given data points and counts the number of values above the given threshold
 * @returns a number of values above the threshold
 * @deprecated chartWithD3
 */
export function getAmountOfValuesOverThreshold({ dataPoints, threshold, isCumulativeSum }) {
    return dataPoints.reduce((amount, currentDataPoint, idx, allDataPoints) => {
        const currentValue = currentDataPoint.value;
        if (!isNil(currentValue)) {
            if (isCumulativeSum) {
                const nextValue = allDataPoints[idx + 1]?.value;
                const delta = !isNil(nextValue) ? nextValue - currentValue : null;
                // for cumulative sums we have to compare the delta to the thresholds
                if (!isNil(delta) && delta > threshold) {
                    amount = amount + 1;
                }
            }
            else if (currentValue > threshold) {
                amount = amount + 1;
            }
        }
        return amount;
    }, 0);
}
